import { isClerkOfflineErrorMessage } from '../api/api.utils';
import { isTestUser, isVrpMember } from '@vrplatform/api';
import { isPlaywrightUserEmail } from './isPlaywrightUserEmail';
import { usesOwnerPortalBootstrap } from '../owner-portal';

/** The platform tunnel's CORS policy excludes customer origins; use the DSN directly there. */
export const getSentryTunnelUrl = (hostname: string) =>
  usesOwnerPortalBootstrap(hostname)
    ? undefined
    : 'https://pomegranate.vrplatform.app';

const MAX_SENTRY_REPLAY_SESSION_SAMPLE_RATE = 0.0005;
const TRACKING_EVENT_ERROR_CAPTURE_WINDOW_MS = 5 * 60 * 1000;
const UUID_IN_URL_PATTERN =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/i;

let lastTrackingEventErrorCaptureAt: number | undefined;
let suppressedTrackingEventErrorCount = 0;

type SentryFrameLike = {
  filename?: string;
  function?: string;
};

type SentryBreadcrumbLike = {
  category?: string;
  message?: string;
  data?: Record<string, unknown>;
};

type SentryExceptionValueLike = {
  type?: string;
  value?: string;
  mechanism?: {
    handled?: boolean;
    type?: string;
  };
  stacktrace?: {
    frames?: SentryFrameLike[];
  };
};

type SentryEventLike = {
  breadcrumbs?: SentryBreadcrumbLike[];
  message?: string;
  fingerprint?: string[];
  location?: string;
  extra?: Record<string, unknown>;
  request?: {
    url?: string;
  };
  tags?: Record<string, unknown>;
  exception?: {
    values?: SentryExceptionValueLike[];
  };
};

type SentryHintLike = {
  originalException?: unknown;
  syntheticException?: unknown;
};

type ShouldDropSentryEventArgs = {
  event: SentryEventLike;
  hint: SentryHintLike | undefined;
};

type TrackingEnvironmentLike = {
  isExtension: boolean;
  isLocalhost: boolean;
  isStorybook: boolean;
};

type SentryReplayEligibilityLike = TrackingEnvironmentLike & {
  isAutomationSession: boolean;
  isProduction: boolean;
  isStaging: boolean;
  pathname: string;
  productionEnabled: boolean;
  stagingEnabled: boolean;
};

type BirdieContactUserLike = {
  id: string;
  email: string;
  name?: string | null;
};

export const isTargetedMixpanelReplayUrlSafe = (href: string): boolean => {
  const url = new URL(href);
  return !url.search && !UUID_IN_URL_PATTERN.test(`${url.pathname}${url.hash}`);
};

export const shouldEnableTargetedMixpanelReplay = ({
  isActor,
  isExtension,
  trackingEnabled,
  user,
  tenantId,
  isTestTeam,
}: {
  isActor: boolean;
  isExtension: boolean;
  trackingEnabled: boolean;
  user: { email: string; id: string };
  tenantId: string | undefined;
  isTestTeam: boolean;
}): boolean =>
  trackingEnabled &&
  !!user.id &&
  !!tenantId &&
  !isActor &&
  !isExtension &&
  !isPlaywrightUserEmail(user.email) &&
  !isTestTeam &&
  !isTestUser({ userEmail: user.email, userId: user.id }) &&
  !isVrpMember({ email: user.email, userId: user.id });

export const createOwnedSessionReplayController = ({
  getActiveReplayId,
  hasOptedOutTracking = () => false,
  onError,
  prepare = async () => {},
  startRecording,
  stopRecording,
}: {
  getActiveReplayId: () => string | undefined;
  hasOptedOutTracking?: () => boolean;
  onError: (error: unknown, operation: 'start' | 'stop') => void;
  prepare?: () => Promise<void>;
  startRecording: () => void | Promise<unknown>;
  stopRecording: () => void | Promise<unknown>;
}) => {
  const defaultOwner = {};
  const owners = new Set<object>();
  let ownsRecording = false;
  let generation = 0;
  let starting: Promise<unknown> | undefined;
  let stopping = Promise.resolve();
  let stopRequested = false;

  const reconcile = async (): Promise<string | undefined> => {
    const version = generation;
    let startingOwned = false;
    try {
      await prepare();
      await stopping;
      if (version !== generation) return;
      if (starting) await starting;
      if (version !== generation) return;
      if (hasOptedOutTracking()) return;
      const activeId = getActiveReplayId();
      if (activeId) return activeId;
      if (!owners.size) return;

      ownsRecording = startingOwned = true;
      starting = Promise.resolve(startRecording());
      await starting;
      starting = undefined;
      if (version !== generation) return;
      const replayId = getActiveReplayId();
      if (!replayId) throw new Error('Mixpanel replay did not start');
      return replayId;
    } catch (error) {
      starting = undefined;
      if (startingOwned) ownsRecording = false;
      onError(error, 'start');
    }
  };

  return {
    start(owner = defaultOwner) {
      owners.add(owner);
      return reconcile();
    },
    stop(owner = defaultOwner) {
      owners.delete(owner);
      if (owners.size) return;
      generation++;
      // Ownership follows the recording session when the SDK rotates replay IDs.
      // All targeted callers share this controller; identity reset releases it.
      if (!ownsRecording) return;
      if (stopRequested) return;
      stopRequested = true;
      try {
        stopping = Promise.resolve(stopRecording())
          .then(() => {
            ownsRecording = false;
            stopRequested = false;
          })
          .catch((error: unknown) => {
            stopRequested = false;
            onError(error, 'stop');
          });
      } catch (error) {
        stopRequested = false;
        onError(error, 'stop');
      }
    },
    reset() {
      generation++;
      owners.clear();
      ownsRecording = false;
    },
  };
};

export const trackingEventErrorCapturePolicy = {
  evaluate(now = Date.now()): {
    shouldCapture: boolean;
    suppressedCount: number;
    windowMs: number;
  } {
    if (
      lastTrackingEventErrorCaptureAt === undefined ||
      now < lastTrackingEventErrorCaptureAt ||
      now - lastTrackingEventErrorCaptureAt >=
        TRACKING_EVENT_ERROR_CAPTURE_WINDOW_MS
    ) {
      const suppressedCount = suppressedTrackingEventErrorCount;
      lastTrackingEventErrorCaptureAt = now;
      suppressedTrackingEventErrorCount = 0;

      return {
        shouldCapture: true,
        suppressedCount,
        windowMs: TRACKING_EVENT_ERROR_CAPTURE_WINDOW_MS,
      };
    }

    suppressedTrackingEventErrorCount += 1;

    return {
      shouldCapture: false,
      suppressedCount: suppressedTrackingEventErrorCount,
      windowMs: TRACKING_EVENT_ERROR_CAPTURE_WINDOW_MS,
    };
  },
  reset() {
    lastTrackingEventErrorCaptureAt = undefined;
    suppressedTrackingEventErrorCount = 0;
  },
};

export const getBirdieContact = (
  user: BirdieContactUserLike
): { id: string; email: string; name?: string } | undefined => {
  if (!user.email) return undefined;

  return {
    id: user.id,
    email: user.email,
    ...(user.name ? { name: user.name } : {}),
  };
};

export const isGoogleTranslateActive = () => {
  if (typeof document === 'undefined') return false;

  try {
    const selectors = [
      'iframe.goog-te-banner-frame',
      '#goog-gt-tt',
      '.goog-te-spinner-pos',
      '.goog-te-combo',
      '.goog-te-banner-frame',
      '.goog-te-gadget',
    ];

    if (selectors.some((selector) => document.querySelector(selector))) {
      return true;
    }

    const htmlClass = document.documentElement?.className ?? '';
    if (
      htmlClass.includes('translated-ltr') ||
      htmlClass.includes('translated-rtl')
    ) {
      return true;
    }

    return !!document.querySelector('script[src*="translate.google.com"]');
  } catch {
    return false;
  }
};

const domMutationMessage =
  /Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node/;
const maximumUpdateDepthMessage = /Maximum update depth exceeded/i;
const rawApiObjectUnhandledRejectionMessage =
  /Object captured as promise rejection with keys:\s*code,\s*(?:context,\s*)?issues,\s*message/i;
const cefSharpCrawlerUpdateRejectionMessage =
  /^Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:update, ParamCount:\d+$/;
// Chrome IndexedDB failure from third-party widgets such as Birdie; first-party code does not use IndexedDB.
const indexedDbInternalErrorMessage = /^UnknownError: Internal error\.$/;
// Mantine's modal Escape listener reads getAttribute from the keydown target;
// extensions dispatch synthetic keydown events on the Document, which lacks it.
const mantineModalEscapeDocumentTargetMessage =
  /^\w+\.target\?\.getAttribute is not a function\b/;
const mixpanelModulePath = /(?:^|\/)node_modules\/mixpanel-browser\//i;
const mixpanelBundleName =
  /(?:^|\/)mixpanel\.module(?:-[^/]+)?\.js(?::\d+(?::\d+)?)?$/i;
const birdieEmbedPath = /\/widget\/embed\//i;
const birdieBundleName = /main\.[^.]+\.js(?::\d+(?::\d+)?)?$/i;
const birdieBundleReference =
  /\/widget\/embed\/main\.[^\s)]+\.js(?::\d+(?::\d+)?)?/i;
const uuidLikeSegment =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const longHexLikeSegment = /^[0-9a-f]{16,}$/i;

type SerializedApiErrorLike = {
  code?: string;
  message?: string;
  issues?: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const getEventMessage = (event: SentryEventLike): string => {
  return event.message || event.exception?.values?.[0]?.value || '';
};

const getHintMessage = (hint: SentryHintLike | undefined): string => {
  const original = hint?.originalException;

  if (typeof original === 'string') return original;

  if (
    original &&
    typeof (original as { message?: unknown }).message === 'string'
  ) {
    return (original as { message: string }).message;
  }

  return '';
};

const isBirdieEmbedFrame = (frame: SentryFrameLike): boolean => {
  const filename = frame.filename;
  if (!filename) return false;

  return birdieEmbedPath.test(filename) && birdieBundleName.test(filename);
};

const isMixpanelFrame = (frame: SentryFrameLike): boolean => {
  const filename = frame.filename?.replaceAll('\\', '/');
  if (!filename) return false;

  return mixpanelModulePath.test(filename) || mixpanelBundleName.test(filename);
};

const isMixpanelEvent = (event: SentryEventLike): boolean => {
  const frames =
    event.exception?.values?.flatMap(
      (value) => value.stacktrace?.frames ?? []
    ) ?? [];
  const nonNativeFrames = frames.filter(
    (frame) => frame.filename !== '[native code]'
  );

  return nonNativeFrames.length > 0 && nonNativeFrames.every(isMixpanelFrame);
};

const isBirdieEmbedLocation = (value: string): boolean => {
  return birdieEmbedPath.test(value) && birdieBundleName.test(value);
};

const hasBirdieEmbedReference = (value: string): boolean => {
  return birdieBundleReference.test(value.replaceAll('\\', '/'));
};

const getStackLikeString = (value: unknown): string => {
  if (!value) return '';

  if (typeof value === 'string') return value;

  if (typeof value !== 'object') return '';

  if (typeof (value as { stack?: unknown }).stack === 'string') {
    return (value as { stack: string }).stack;
  }

  if ('reason' in value) {
    return getStackLikeString((value as { reason?: unknown }).reason);
  }

  if ('error' in value) {
    return getStackLikeString((value as { error?: unknown }).error);
  }

  return '';
};

const unwrapUnhandledRejectionValue = (value: unknown): unknown => {
  if (!isRecord(value)) return value;

  if ('reason' in value) {
    return unwrapUnhandledRejectionValue(value.reason);
  }

  if ('error' in value) {
    return unwrapUnhandledRejectionValue(value.error);
  }

  return value;
};

const isSerializedApiError = (
  value: unknown
): value is SerializedApiErrorLike => {
  if (!isRecord(value)) return false;

  const message = value.message;
  if (typeof message !== 'string' || message.trim().length === 0) return false;

  return 'code' in value || 'issues' in value;
};

const getUnhandledApiRejectionPayload = (
  event: SentryEventLike,
  hint: SentryHintLike | undefined
): SerializedApiErrorLike | undefined => {
  const candidates = [
    event.extra?.__serialized__,
    hint?.originalException,
    hint?.syntheticException,
  ];

  for (const candidate of candidates) {
    const unwrapped = unwrapUnhandledRejectionValue(candidate);
    if (isSerializedApiError(unwrapped)) {
      return unwrapped;
    }
  }

  return undefined;
};

const getEventUrl = (event: SentryEventLike): string | undefined => {
  const taggedUrl = event.tags?.url;
  return (
    event.request?.url ||
    (typeof taggedUrl === 'string' ? taggedUrl : undefined)
  );
};

const normalizeApiRejectionRoute = (url: string | undefined): string => {
  if (!url) return 'unknown-route';

  try {
    const pathname = new URL(url).pathname || '/';
    const segments = pathname
      .split('/')
      .filter(Boolean)
      .map((segment) =>
        uuidLikeSegment.test(segment) || longHexLikeSegment.test(segment)
          ? ':id'
          : segment
      );

    return segments.length > 0 ? `/${segments.join('/')}` : '/';
  } catch {
    if (url.startsWith('/')) return url;
    return 'unknown-route';
  }
};

const isBirdieEvent = (
  event: SentryEventLike,
  hint: SentryHintLike | undefined
): boolean => {
  const frames =
    event.exception?.values?.flatMap(
      (value) => value.stacktrace?.frames ?? []
    ) ?? [];

  if (frames.some(isBirdieEmbedFrame)) {
    return true;
  }

  if (event.location && isBirdieEmbedLocation(event.location)) {
    return true;
  }

  const hasBirdieBreadcrumb = event.breadcrumbs?.some((breadcrumb) => {
    const breadcrumbUrl = breadcrumb.data?.url;
    return [breadcrumb.message, breadcrumbUrl].some(
      (value) =>
        typeof value === 'string' &&
        (/\.birdie\.so\/widget\/embed\//i.test(value) ||
          /\/widget\/embed\//i.test(value))
    );
  });

  if (hasBirdieBreadcrumb && !frames.some(isFirstPartyFrame)) {
    return true;
  }

  return [hint?.originalException, hint?.syntheticException].some((value) => {
    const stack = getStackLikeString(value);
    return stack ? hasBirdieEmbedReference(stack) : false;
  });
};

const normalizeFrameFilename = (filename: string): string => {
  const normalized = filename.replaceAll('\\', '/');
  const withoutHash = normalized.split('#')[0] ?? normalized;
  const withoutQuery = withoutHash.split('?')[0] ?? withoutHash;
  const withoutLine = withoutQuery.replace(/:\d+:\d+$/, '');

  const appSrcMatch = withoutLine.match(/(?:^|\/)(app\/src\/.+)$/);
  if (appSrcMatch?.[1]) return appSrcMatch[1];

  const srcMatch = withoutLine.match(/(?:^|\/)(src\/.+)$/);
  if (srcMatch?.[1]) return srcMatch[1];

  const libsSrcMatch = withoutLine.match(/(?:^|\/)(libs\/[^/]+\/src\/.+)$/);
  if (libsSrcMatch?.[1]) return libsSrcMatch[1];

  return withoutLine.replace(/^(\.\.\/)+/, '');
};

const isFirstPartyFrame = (frame: SentryFrameLike): boolean => {
  const filename = frame.filename;
  if (!filename) return false;
  if (filename.includes('node_modules')) return false;

  const normalized = normalizeFrameFilename(filename);

  return (
    normalized.startsWith('app/src/') ||
    normalized.startsWith('src/') ||
    normalized.startsWith('libs/')
  );
};

const getMaximumUpdateDepthComponentKey = (event: SentryEventLike): string => {
  const frames =
    event.exception?.values?.flatMap(
      (value) => value.stacktrace?.frames ?? []
    ) ?? [];

  const firstPartyFrames = frames.filter(isFirstPartyFrame);

  const appFrame = firstPartyFrames.find((frame) => {
    const filename = frame.filename;
    if (!filename) return false;

    const normalized = normalizeFrameFilename(filename);
    return normalized.startsWith('app/src/') || normalized.startsWith('src/');
  });

  if (appFrame?.filename) {
    return normalizeFrameFilename(appFrame.filename);
  }

  const fallbackFrame = firstPartyFrames[0];
  if (fallbackFrame?.filename) {
    return normalizeFrameFilename(fallbackFrame.filename);
  }

  return 'unknown-component';
};

export const withMaximumUpdateDepthFingerprint = <T extends SentryEventLike>(
  event: T
): T => {
  if (!maximumUpdateDepthMessage.test(getEventMessage(event))) {
    return event;
  }

  event.fingerprint = [
    '{{ default }}',
    'maximum-update-depth-exceeded',
    getMaximumUpdateDepthComponentKey(event),
  ];

  return event;
};

export const withUnhandledApiRejectionFingerprint = <T extends SentryEventLike>(
  event: T,
  hint: SentryHintLike | undefined
): T => {
  const eventMessage = getEventMessage(event);
  if (!rawApiObjectUnhandledRejectionMessage.test(eventMessage)) {
    return event;
  }

  const payload = getUnhandledApiRejectionPayload(event, hint);
  if (!payload?.message) return event;

  const code =
    typeof payload.code === 'string' && payload.code.trim().length > 0
      ? payload.code
      : 'unknown-code';
  const route = normalizeApiRejectionRoute(getEventUrl(event));
  const title = `Unhandled API rejection (${code}): ${payload.message}`;

  event.message = title;
  if (event.exception?.values?.[0]) {
    event.exception.values[0].value = title;
  }

  event.fingerprint = [
    '{{ default }}',
    'unhandled-api-rejection',
    code,
    payload.message,
    route,
  ];
  event.tags = {
    ...event.tags,
    error_kind: 'api-object-rejection',
    error_source: 'global_unhandledrejection',
    api_error_code: code,
    api_error_route: route,
  };
  event.extra = {
    ...event.extra,
    apiRejection: {
      code,
      message: payload.message,
      route,
      source: 'global_unhandledrejection',
    },
  };

  return event;
};

export const isTrackingEnabled = ({
  isExtension,
  isLocalhost,
  isStorybook,
}: TrackingEnvironmentLike): boolean => {
  return !isExtension && !isLocalhost && !isStorybook;
};

export const isSentryAutomationSession = ({
  isWebdriver,
  search,
}: {
  isWebdriver: boolean;
  search: string;
}): boolean => {
  return (
    isWebdriver || new URLSearchParams(search).get('checkly_test') === 'true'
  );
};

export const shouldInitializeSentryReplay = ({
  isAutomationSession,
  isExtension,
  isLocalhost,
  isProduction,
  isStaging,
  isStorybook,
  pathname,
  productionEnabled,
  stagingEnabled,
}: SentryReplayEligibilityLike): boolean => {
  const isEnabledEnvironment =
    (isProduction && productionEnabled) || (isStaging && stagingEnabled);
  // Trim trailing slashes without a regex that backtracks on long slash runs.
  let pathnameEnd = pathname.length;
  while (pathnameEnd > 1 && pathname[pathnameEnd - 1] === '/') pathnameEnd--;
  const normalizedPathname = pathname.slice(0, pathnameEnd) || '/';

  return (
    isEnabledEnvironment &&
    !isExtension &&
    !isLocalhost &&
    !isStorybook &&
    !isAutomationSession &&
    normalizedPathname !== '/recorder'
  );
};

export const getSentryReplaySessionSampleRate = (
  configuredRate: string | undefined
): number => {
  const normalizedRate = configuredRate?.trim();
  if (!normalizedRate) return 0;

  const sampleRate = Number(normalizedRate);
  if (
    !Number.isFinite(sampleRate) ||
    sampleRate < 0 ||
    sampleRate > MAX_SENTRY_REPLAY_SESSION_SAMPLE_RATE
  ) {
    throw new RangeError(
      `VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE must be between 0 and ${MAX_SENTRY_REPLAY_SESSION_SAMPLE_RATE}`
    );
  }

  return sampleRate;
};

export const shouldSampleSentryReplayForError = (
  _event: SentryEventLike
): boolean => true;

export const getSentryTraceSampleRate = ({
  inheritOrSampleWith,
  isProduction,
}: {
  inheritOrSampleWith: (fallbackSampleRate: number) => number;
  isProduction: boolean;
}): number => {
  return inheritOrSampleWith(isProduction ? 0.1 : 0.01);
};

export const shouldCreateSentryRequestSpan = ({
  currentOrigin,
  graphqlUrl,
  requestUrl,
}: {
  currentOrigin: string;
  graphqlUrl: string;
  requestUrl: string;
}): boolean => {
  try {
    const currentUrl = new URL(currentOrigin);
    const parsedGraphqlUrl = new URL(graphqlUrl);
    const parsedRequestUrl = new URL(requestUrl, currentUrl);

    if (
      parsedRequestUrl.origin === currentUrl.origin &&
      (parsedRequestUrl.pathname === '/trpc' ||
        parsedRequestUrl.pathname.startsWith('/trpc/'))
    ) {
      return true;
    }

    const isFirstPartyApiRequest =
      parsedRequestUrl.protocol === 'https:' &&
      (parsedRequestUrl.hostname === 'api.vrplatform.app' ||
        parsedRequestUrl.hostname.endsWith('.api.vrplatform.app') ||
        /^api\.[a-z0-9-]+\.vrplatform\.app$/.test(parsedRequestUrl.hostname) ||
        parsedRequestUrl.hostname === 'staging.api.edge.vrplatform.app');

    if (isFirstPartyApiRequest) {
      return true;
    }

    return (
      parsedRequestUrl.origin === parsedGraphqlUrl.origin &&
      parsedRequestUrl.pathname === parsedGraphqlUrl.pathname
    );
  } catch {
    return false;
  }
};

export const shouldDropSentryEvent = ({
  event,
  hint,
}: ShouldDropSentryEventArgs): boolean => {
  if (
    event.exception?.values?.some(
      ({ type, value }) =>
        type === 'TRPCClientError' &&
        /^(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.)$/.test(
          value?.trim() ?? ''
        )
    )
  ) {
    return true;
  }

  if (
    event.tags?.source === 'tracking-event-delivery' &&
    event.tags?.operation === 'post-internal-events'
  ) {
    return true;
  }

  if (isMixpanelEvent(event)) {
    return true;
  }

  if (isBirdieEvent(event, hint)) {
    return true;
  }

  if (isGoogleTranslateActive()) {
    return true;
  }

  const eventMessage = getEventMessage(event);
  const hintMessage = getHintMessage(hint);

  return (
    isClerkOfflineErrorMessage(eventMessage) ||
    isClerkOfflineErrorMessage(hintMessage) ||
    cefSharpCrawlerUpdateRejectionMessage.test(eventMessage) ||
    cefSharpCrawlerUpdateRejectionMessage.test(hintMessage) ||
    indexedDbInternalErrorMessage.test(eventMessage) ||
    domMutationMessage.test(eventMessage) ||
    domMutationMessage.test(hintMessage) ||
    mantineModalEscapeDocumentTargetMessage.test(eventMessage) ||
    mantineModalEscapeDocumentTargetMessage.test(hintMessage)
  );
};
